import FollowSellerButton from '@c/buttons/FollowSellerButton';
import { CloseIcon } from '@c/icons';

interface SellerFollowPopupProps {
  sellerId: string;
  sellerName: string;
  onDismiss: () => void;
}

/**
 * First-visit, one-time nudge that introduces the Follow-seller feature (GB
 * #691). Non-blocking and dismissible by design — it is a courtesy nudge, not a
 * gate, and it is shown a single time per person (the "already seen" gating is
 * the caller's job; this component is purely presentational).
 *
 * Anchored to the bottom of the viewport: bottom-center on mobile (thumb reach,
 * clear of the top content), bottom-right on desktop. It never covers the
 * primary page content, so a buyer can ignore it and keep shopping.
 */
const SellerFollowPopup = ({
  sellerId,
  sellerName,
  onDismiss,
}: SellerFollowPopupProps) => {
  return (
    <div
      role="dialog"
      aria-label="Follow this seller"
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-[1.6rem] pb-[1.6rem] sm:inset-x-auto sm:right-[2.4rem] sm:justify-end"
    >
      <div className="relative w-full max-w-[34rem] rounded-[1.2rem] border border-brand-lightest-gray bg-brand-white p-[2rem] shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="absolute right-[1.2rem] top-[1.2rem] text-brand-gray transition-colors hover:text-brand-black"
        >
          <CloseIcon height={16} width={16} />
        </button>

        <div className="flex items-start gap-[1.2rem] pr-[1.2rem]">
          {/* Bell accent in brand orange — the only orange on the card, so it
              reads as the feature's mark, not decoration. */}
          <span className="mt-[0.2rem] shrink-0 text-[#C75008]">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9Z M13.7 21a2 2 0 0 1-3.4 0"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>

          <div className="flex flex-col gap-[1.2rem]">
            <div className="flex flex-col gap-[0.4rem]">
              <p className="text-[1.5rem] font-semibold leading-snug text-brand-black">
                Follow {sellerName} for new-gear alerts
              </p>
              <p className="text-[1.2rem] leading-snug text-brand-light-black">
                We&apos;ll notify you the moment they list something new.
              </p>
            </div>
            <div className="flex items-center gap-[0.8rem]">
              <FollowSellerButton sellerId={sellerId} height="small" width="normal" />
              <button
                type="button"
                onClick={onDismiss}
                className="text-[1.3rem] font-semibold text-brand-gray transition-colors hover:text-brand-black"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerFollowPopup;
